// DeliveryCheckPrompt — asks a buyer how a delivered marketplace deal went.
//
// Carrier tracking (marketplace-tracking-webhook) now moves a deal to
// 'delivered' on its own, so the buyer may not be in the thread when it
// happens. This prompt meets them wherever they land next: any signed-in page
// load, tab refocus or navigation re-checks for a delivered deal they haven't
// confirmed.
//
//   All good        -> txn_mark_complete (same RPC as the thread's button)
//   Something wrong -> message the seller, take 48 more hours, or report it
//                      (txn_report_problem -> 'disputed', out of the 48h rules)
//   Ask me later    -> hidden for this browser session; back on the next visit
//
// Mounted beside <App /> in index.js with its own auth listener, so it works on
// every route without threading through App's early returns. Sits under
// UserNoticeModal (z-index) so an acknowledge-required notice always wins.
// Escrow.com deals are confirmed on Escrow.com, so they're skipped.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { imageUrl } from '../CardImage';

const fontDisplay = "'Orbitron', sans-serif";
const fontBody = "'Rajdhani', sans-serif";
const fontMono = "'Share Tech Mono', monospace";
const LATER_KEY = 'ck-delivery-check-later';

function laterIds() {
  try { return JSON.parse(sessionStorage.getItem(LATER_KEY) || '[]'); } catch { return []; }
}
function rememberLater(id) {
  try { sessionStorage.setItem(LATER_KEY, JSON.stringify([...new Set([...laterIds(), id])])); } catch { /* private mode: shows again next load */ }
}
function navTo(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
// The hourly auto_flag_stale_transactions job skips deals with an extension,
// so only an unextended deal has a deadline to show.
function hoursLeft(deliveredAt, extended) {
  if (!deliveredAt || extended) return null;
  const end = new Date(deliveredAt).getTime() + 48 * 3600 * 1000;
  return Math.max(0, Math.round((end - Date.now()) / 3600000));
}

export default function DeliveryCheckPrompt() {
  const [userId, setUserId] = useState(null);
  const [deal, setDeal] = useState(null); // { txn, snap, seller, me }
  const [step, setStep] = useState('ask'); // ask | problem | done
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [path, setPath] = useState(window.location.pathname);
  // A re-check after completing must not wipe the "Deal complete" screen.
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUserId(data?.session?.user?.id || null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => setUserId(session?.user?.id || null));
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => { subscription.unsubscribe(); window.removeEventListener('popstate', onPop); };
  }, []);

  const check = useCallback(async () => {
    if (!userId) { setDeal(null); return; }
    const { data: txns, error: err } = await supabase
      .from('transactions')
      .select('*')
      .eq('buyer_id', userId)
      .eq('status', 'delivered')
      .neq('payment_method', 'escrow')
      .is('buyer_confirmed_at', null)
      .order('delivered_at', { ascending: true });
    if (err) { console.warn('[DeliveryCheckPrompt] lookup failed (non-fatal):', err.message); return; }
    const skip = laterIds();
    const txn = (txns || []).find(t => !skip.includes(t.id));
    if (!txn) { if (stepRef.current !== 'done') setDeal(null); return; }
    const [{ data: conv }, { data: cp }, { data: me }, { data: trk }] = await Promise.all([
      supabase.from('conversations').select('card_snapshot').eq('id', txn.conversation_id).maybeSingle(),
      supabase.rpc('get_conversation_counterparty', { p_conversation_id: txn.conversation_id }),
      supabase.from('profiles').select('username').eq('id', userId).maybeSingle(),
      supabase.from('shipment_tracking').select('carrier, events, status').eq('transaction_id', txn.id).maybeSingle(),
    ]);
    if (stepRef.current === 'done') return;
    setDeal(prev => (prev?.txn?.id === txn.id ? prev : {
      txn,
      snap: conv?.card_snapshot || {},
      seller: cp?.[0]?.username ? `@${cp[0].username}` : 'the seller',
      me: me?.username ? `@${me.username}` : 'The buyer',
      carrier: trk?.carrier || txn.carrier || null,
      deliveredPlace: [trk?.events?.[0]?.city, trk?.events?.[0]?.state].filter(Boolean).join(', '),
      // Carrier-confirmed deals complete on their own after 48h of silence
      // (process_stale_deliveries); others still auto-flag.
      autoCompletes: trk?.status === 'delivered',
    }));
  }, [userId]);

  useEffect(() => {
    check();
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [check, path]);

  // New deal on screen -> start at the question.
  const dealId = deal?.txn?.id;
  useEffect(() => { setStep('ask'); setError(null); }, [dealId]);

  const postSystem = useCallback(async (text) => {
    const cid = deal?.txn?.conversation_id;
    if (!cid) return;
    await supabase.from('messages').insert({ conversation_id: cid, sender_id: null, body: text });
    await supabase.from('conversations').update({ updated_at: new Date().toISOString() }).eq('id', cid);
  }, [deal]);

  if (!deal) return null;
  // Already looking at this deal's thread: its action card covers it.
  if (step !== 'done' && path === `/messages/${deal.txn.conversation_id}`) return null;

  const { txn, snap, seller, me, carrier, deliveredPlace, autoCompletes } = deal;
  const cardLabel = [snap.setName, snap.cardN ? `#${snap.cardN}` : null].filter(Boolean).join(' ') || 'your card';
  const extensionUsed = !!txn.flag_extension_requested_buyer;
  const left = hoursLeft(txn.delivered_at, extensionUsed);
  const img = txn.seller_id && txn.card_key ? imageUrl(txn.seller_id, txn.card_key, 'front', snap.photos_updated_at || snap.has_photos || undefined) : null;
  const deliveredOn = txn.delivered_at
    ? new Date(txn.delivered_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    : null;

  const close = () => { stepRef.current = 'ask'; setStep('ask'); setDeal(null); };
  const later = () => { rememberLater(txn.id); close(); };
  const openThread = () => { rememberLater(txn.id); close(); navTo(`/messages/${txn.conversation_id}`); };

  async function allGood() {
    setBusy('complete'); setError(null);
    const { data, error: err } = await supabase.rpc('txn_mark_complete', { p_transaction_id: txn.id });
    if (err || !data) {
      setError("That didn't save. Check your connection and try again.");
      setBusy(null);
      return;
    }
    await postSystem('Both parties confirmed · Deal complete. Trust badge updated.');
    setBusy(null);
    setStep('done');
  }

  async function moreTime() {
    setBusy('extend'); setError(null);
    const { data, error: err } = await supabase.rpc('txn_request_extension', { p_transaction_id: txn.id });
    if (err || !data) {
      setError("That didn't save. Check your connection and try again.");
      setBusy(null);
      return;
    }
    await postSystem(`${me} requested 48 more hours to confirm. Extension applied.`);
    setBusy(null);
    rememberLater(txn.id);
    close();
  }

  async function report() {
    setBusy('report'); setError(null);
    const { data, error: err } = await supabase.rpc('txn_report_problem', { p_transaction_id: txn.id });
    if (err || !data) {
      setError("That didn't save. Check your connection and try again.");
      setBusy(null);
      return;
    }
    await postSystem(`${me} reported a problem with this delivery. CollectionKeeper is reviewing the deal.`);
    setBusy(null);
    rememberLater(txn.id);
    window.location.href = `mailto:keeper18@collectionkeeper.org?subject=${encodeURIComponent(`Problem with transaction ${txn.id}`)}&body=${encodeURIComponent("Please describe what's wrong with this delivery so we can look into it.")}`;
    close();
  }

  return (
    <div style={P.overlay} role="dialog" aria-modal="true" aria-labelledby="ck-delivery-check-title">
      <div style={P.box}>
        <div style={P.eyebrow}>// DELIVERY CHECK-IN //</div>

        <div style={P.cardRow}>
          <Thumb src={img} />
          <div style={{ minWidth: 0 }}>
            <div style={P.set}>{snap.setName || 'Marketplace purchase'}</div>
            <div style={P.card}>Card #{snap.cardN || '?'}</div>
            {snap.parallelName && <span style={P.parallel}>{snap.parallelName}</span>}
          </div>
        </div>

        {step === 'ask' && (
          <>
            <h2 id="ck-delivery-check-title" style={P.title}>Your card arrived. How did it go?</h2>
            <p style={P.text}>
              {carrier ? `${carrier} delivered` : 'Delivered:'} {cardLabel} from {seller}
              {deliveredOn ? ` on ${deliveredOn}` : ''}{deliveredPlace ? ` in ${deliveredPlace}` : ''}.
              Check that it matches the listing.
            </p>
            {left != null && (
              <div style={P.timer}>
                {left} hours left to respond{autoCompletes ? '. No answer completes the deal.' : ''}
              </div>
            )}
            {error && <div style={P.error}>{error}</div>}
            <button style={P.good} onClick={allGood} disabled={!!busy} autoFocus>
              {busy === 'complete' ? 'Completing…' : "I'm happy with it"}
            </button>
            <button style={P.bad} onClick={() => setStep('problem')} disabled={!!busy}>
              Something's wrong
            </button>
            <button style={P.later} onClick={later} disabled={!!busy}>Ask me later</button>
          </>
        )}

        {step === 'problem' && (
          <>
            <h2 id="ck-delivery-check-title" style={P.title}>Sorry about that. What would help?</h2>
            <p style={P.text}>
              Most problems are fixed fastest by messaging {seller}.
              {autoCompletes && !extensionUsed
                ? ' Messaging alone keeps the 48-hour clock running: take more time or report it to keep the deal open.'
                : ''}
            </p>
            {left != null && <div style={P.timer}>{left} hours left to respond</div>}
            {error && <div style={P.error}>{error}</div>}
            <button style={P.option} onClick={openThread} disabled={!!busy}>Message {seller}</button>
            {!extensionUsed && (
              <button style={P.option} onClick={moreTime} disabled={!!busy}>
                {busy === 'extend' ? 'Adding time…' : 'Take 48 more hours'}
              </button>
            )}
            <button style={{ ...P.option, ...P.danger }} onClick={report} disabled={!!busy}>
              {busy === 'report' ? 'Reporting…' : 'Report a problem to CollectionKeeper'}
            </button>
            <button style={P.later} onClick={() => setStep('ask')} disabled={!!busy}>Back</button>
          </>
        )}

        {step === 'done' && (
          <>
            <h2 id="ck-delivery-check-title" style={{ ...P.title, color: 'var(--ok-2)' }}>Deal complete</h2>
            <p style={P.text}>Thanks for confirming. {seller}'s trust history now shows a clean sale.</p>
            <button style={P.good} onClick={close} autoFocus>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

function Thumb({ src }) {
  const [failed, setFailed] = useState(false);
  return (
    <div style={P.thumb}>
      {src && !failed
        ? <img src={src} alt="" onError={() => setFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : 'CARD'}
    </div>
  );
}

const btn = { width: '100%', padding: '12px 16px', borderRadius: 3, fontFamily: fontMono, fontSize: 13, letterSpacing: 1, cursor: 'pointer', marginTop: 10 };
const P = {
  overlay: { position: 'fixed', inset: 0, zIndex: 9990, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' },
  box: { background: 'var(--panel)', border: '1px solid var(--border-2)', borderTop: '2px solid var(--ok-2)', borderRadius: 4, maxWidth: 440, width: '100%', margin: 'auto', padding: '22px 22px 18px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  eyebrow: { fontFamily: fontMono, fontSize: 10, letterSpacing: 2, color: 'var(--cyan)', marginBottom: 14 },
  cardRow: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 },
  thumb: { width: 54, height: 76, flexShrink: 0, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(var(--brand-gold-rgb),0.45)', background: 'var(--bg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: fontMono, fontSize: 8, color: 'var(--muted-5)' },
  set: { fontFamily: fontMono, fontSize: 10, color: 'var(--muted-5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  card: { fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: 'var(--text-bright)', margin: '3px 0 4px' },
  parallel: { display: 'inline-block', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--peach)', border: '0.5px solid var(--sketch-orange)', padding: '1px 5px', borderRadius: 2 },
  title: { fontFamily: fontDisplay, fontSize: 15, fontWeight: 600, letterSpacing: 0.5, color: 'var(--text-bright)', margin: '0 0 8px', lineHeight: 1.35 },
  text: { fontFamily: fontBody, fontSize: 16, color: 'var(--text)', lineHeight: 1.5, margin: '0 0 10px' },
  timer: { fontFamily: fontMono, fontSize: 11, color: 'var(--amber)', marginBottom: 4 },
  error: { fontFamily: fontMono, fontSize: 11, color: 'var(--red-2)', margin: '6px 0 0', lineHeight: 1.5 },
  good: { ...btn, background: 'rgba(var(--ok-2-rgb),0.15)', color: 'var(--ok)', border: '1px solid rgba(var(--ok-2-rgb),0.6)' },
  bad: { ...btn, background: 'transparent', color: 'var(--text-bright)', border: '1px solid var(--border-2)' },
  option: { ...btn, background: 'transparent', color: 'var(--text-bright)', border: '1px solid var(--border-2)', textAlign: 'left' },
  danger: { color: '#ef4444', borderColor: 'rgba(239,68,68,0.45)' },
  later: { ...btn, background: 'transparent', color: 'var(--muted-5)', border: 'none', fontSize: 12, marginTop: 6 },
};
